import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '@/App';
import { useConfig } from '@/context/ConfigContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Activity, Users, Shield, FileText, Heart, Briefcase,
  Save, Calendar, CheckCircle2, Plus, Edit2, Trash2, History,
  Stethoscope, AlertTriangle, TrendingUp, Upload, Brain, Sparkles, Loader2, 
  FileUp, Lightbulb, Target, ClipboardList, Building2, Clock, DollarSign
} from 'lucide-react';
import AIContent from '@/components/AIContent';
import { ExportDropdown } from '@/components/ExportDropdown';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '@/components/ModernUI';
import { DataHistoryPanel } from '@/components/DataHistory';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FormField = ({ label, children, hint, required }) => (
  <div className="space-y-2">
    <Label className="text-sm font-medium flex items-center gap-1">
      {label}
      {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const FormSection = ({ title, description, icon: Icon, children }) => (
  <div className="form-section animate-fade-in">
    <div className="mb-6 flex items-start gap-3">
      {Icon && (
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
          <Icon className="h-5 w-5 text-primary" />
        </div>
      )}
      <div>
        <h3 className="text-lg font-semibold">{title}</h3>
        {description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {children}
    </div>
  </div>
);

// Validation helper component - uses validation targets from config
const ValidationWarning = ({ fieldName, value, validationTargets }) => {
  const validation = validationTargets[fieldName];
  if (!validation || value === '' || value === null || value === undefined) return null;
  
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return null;
  
  // Check if value is below target for metrics where higher is better
  const isPercentMetric = fieldName.includes('percent') || fieldName.includes('_percent');
  const isCountMetric = fieldName.includes('falls') || fieldName.includes('errors') || fieldName.includes('overtime');
  
  let warning = null;
  let severity = 'warning'; // 'warning' or 'critical'
  
  if (isCountMetric) {
    // For counts like falls, medication errors - lower is better
    if (numValue > validation.target * 2) {
      warning = `${validation.label || fieldName} is significantly above target (${validation.target})`;
      severity = 'critical';
    } else if (numValue > validation.target) {
      warning = `${validation.label || fieldName} is above target (${validation.target})`;
    }
  } else if (isPercentMetric) {
    // For percentages - higher is better
    const deviation = validation.target - numValue;
    if (deviation > 15) {
      warning = `${validation.label || fieldName} at ${numValue}% is significantly below the ${validation.target}% target - is this correct?`;
      severity = 'critical';
    } else if (deviation > 5) {
      warning = `${validation.label || fieldName} at ${numValue}% is below the ${validation.target}% target`;
    }
  } else {
    // For other metrics like ADC, ALOS
    if (numValue < validation.min) {
      warning = `${validation.label || fieldName} seems unusually low`;
      severity = 'warning';
    } else if (numValue > validation.max) {
      warning = `${validation.label || fieldName} seems unusually high`;
      severity = 'warning';
    }
  }
  
  if (!warning) return null;
  
  return (
    <div className={`mt-2 flex items-start gap-2 p-2 rounded-md text-xs ${
      severity === 'critical' 
        ? 'bg-red-50 border border-red-200 text-red-700 dark:bg-red-950 dark:border-red-800 dark:text-red-300'
        : 'bg-amber-50 border border-amber-200 text-amber-700 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-300'
    }`}>
      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
      <span>{warning}</span>
    </div>
  );
};

export default function DataEntry() {
  const { token, user } = useAuth();
  const { getValidationTargets } = useConfig();
  
  // Get validation targets from config context
  const validationTargets = useMemo(() => getValidationTargets(), [getValidationTargets]);
  const [activeTab, setActiveTab] = useState('clinical');
  const [saving, setSaving] = useState(false);
  const [periodType, setPeriodType] = useState('weekly');
  const [periodDate, setPeriodDate] = useState(new Date().toISOString().split('T')[0]);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [metricsHistory, setMetricsHistory] = useState([]);
  const [editingMetric, setEditingMetric] = useState(null);
  
  // Upload & Analysis State
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadText, setUploadText] = useState('');
  const [uploadDataType, setUploadDataType] = useState('clinical');
  const [uploading, setUploading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const fileInputRef = useRef(null);

  // Clinical Metrics Data with new fields
  const [clinicalData, setClinicalData] = useState({
    // Symptom Management
    pain_controlled_48h_percent: '',
    dyspnea_improvement_48h_percent: '',
    anxiety_improvement_percent: '',
    uncontrolled_symptom_escalations: '',
    prn_medication_timeliness_percent: '',
    // Visits & Care
    visits_completed_vs_planned_percent: '',
    missed_visit_rate_percent: '',
    avg_visits_per_rn_per_day: '',
    lpn_utilization_rate_percent: '',
    prn_utilization_rate_percent: '',
    // HOPE / Compliance
    hope_completed_on_time_percent: '',
    sfv_triggered_appropriately_percent: '',
    moderate_severe_followups_completed_percent: '',
    timely_idg_completion_percent: '',
    // Enhanced Metrics
    wounds_count: '',
    wounds_healing_percent: '',
    falls_total: '',
    infections_count: '',
    nursing_visit_gaps_7days: '',
    gip_utilization_percent: '',
    chc_utilization_percent: '',
    burdensome_transitions: '',
    cahps_recommendation_score: '',
    // HVLDL - Hospice Visits in Last Days of Life
    hvldl_2_days_percent: '',      // RN visit in last 2 days
    hvldl_3_days_percent: '',      // RN visit in last 3 days
    hvldl_7_days_percent: '',      // Any visit in last 7 days
    hvldl_median_visits_last_week: '', // Median visits in final week
    // Length of Stay & Census
    median_length_of_stay: '',
    mean_length_of_stay: '',
    median_adc: '',
    live_discharges_percent: '',
    total_adc: ''
  });

  // Facility Pipeline - Top 10 Referral Sources
  const [referralSources, setReferralSources] = useState([
    { facility_name: '', referrals: '', conversions: '', notes: '' },
    { facility_name: '', referrals: '', conversions: '', notes: '' },
    { facility_name: '', referrals: '', conversions: '', notes: '' },
    { facility_name: '', referrals: '', conversions: '', notes: '' },
    { facility_name: '', referrals: '', conversions: '', notes: '' },
    { facility_name: '', referrals: '', conversions: '', notes: '' },
    { facility_name: '', referrals: '', conversions: '', notes: '' },
    { facility_name: '', referrals: '', conversions: '', notes: '' },
    { facility_name: '', referrals: '', conversions: '', notes: '' },
    { facility_name: '', referrals: '', conversions: '', notes: '' }
  ]);

  const [safetyData, setSafetyData] = useState({
    falls_with_injury: '', falls_without_injury: '', medication_errors_near_miss: '',
    medication_errors_actual_harm: '', hospitalizations: '', er_visits: '',
    after_hours_escalations: '', level_3_incidents: '', level_4_incidents: '',
    level_5_incidents: '', rca_completed_percent: '', patient_days: ''
  });

  const [regulatoryData, setRegulatoryData] = useState({
    cti_timeliness_percent: '', f2f_correct_window_percent: '', poc_updates_on_time_percent: '',
    orders_signed_on_time_percent: '', documentation_completeness_score: '',
    adr_favorable: '', adr_unfavorable: '', appeals_pending: '',
    appeals_successful: '', appeals_unsuccessful: ''
  });

  const [patientExpData, setPatientExpData] = useState({
    cahps_communication: '', cahps_pain_symptom_help: '', cahps_emotional_spiritual_support: '',
    cahps_timeliness_of_care: '', complaints_received: '', complaints_resolved_on_time: '',
    compliment_themes: '', complaint_themes: ''
  });

  const [staffData, setStaffData] = useState({
    rn_turnover_rate_percent: '', sw_turnover_rate_percent: '', cna_turnover_rate_percent: '',
    rn_vacancies: '', sw_vacancies: '', cna_vacancies: '', chaplain_vacancies: '',
    overtime_hours: '', staff_injury_incidents: '', education_completion_rate_percent: '',
    competency_review_completion_percent: '',
    prn_utilization: '' // PRN staff utilization percentage
  });

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/metrics/clinical/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMetricsHistory(data);
      }
    } catch (error) {
      toast.error('Failed to load history');
    }
  };

  const saveData = async (endpoint, data, setData) => {
    setSaving(true);
    
    const processedData = { ...data };
    Object.keys(processedData).forEach(key => {
      const val = processedData[key];
      if (val === '') {
        processedData[key] = key.includes('themes') ? [] : 0;
      } else if (key.includes('themes')) {
        processedData[key] = val.split(',').map(s => s.trim()).filter(Boolean);
      } else if (!isNaN(parseFloat(val))) {
        processedData[key] = parseFloat(val);
      }
    });

    try {
      const response = await fetch(`${API_URL}/api/metrics/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...processedData,
          period_type: periodType,
          period_date: periodDate
        })
      });

      if (response.ok) {
        toast.success('Data saved successfully', {
          description: `${endpoint.replace('-', ' ')} metrics recorded for ${periodDate}`
        });
      } else {
        const error = await response.json();
        toast.error('Failed to save', { description: error.detail || 'Unknown error' });
      }
    } catch (error) {
      toast.error('Connection error', { description: 'Unable to save data' });
    } finally {
      setSaving(false);
    }
  };

  const deleteMetric = async (metricId) => {
    if (!window.confirm('Delete this metric entry?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/metrics/clinical/${metricId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Metric deleted');
        fetchHistory();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const loadMetricForEdit = (metric) => {
    setClinicalData({
      pain_controlled_48h_percent: metric.pain_controlled_48h_percent?.toString() || '',
      dyspnea_improvement_48h_percent: metric.dyspnea_improvement_48h_percent?.toString() || '',
      anxiety_improvement_percent: metric.anxiety_improvement_percent?.toString() || '',
      uncontrolled_symptom_escalations: metric.uncontrolled_symptom_escalations?.toString() || '',
      prn_medication_timeliness_percent: metric.prn_medication_timeliness_percent?.toString() || '',
      visits_completed_vs_planned_percent: metric.visits_completed_vs_planned_percent?.toString() || '',
      missed_visit_rate_percent: metric.missed_visit_rate_percent?.toString() || '',
      avg_visits_per_rn_per_day: metric.avg_visits_per_rn_per_day?.toString() || '',
      lpn_utilization_rate_percent: metric.lpn_utilization_rate_percent?.toString() || '',
      prn_utilization_rate_percent: metric.prn_utilization_rate_percent?.toString() || '',
      hope_completed_on_time_percent: metric.hope_completed_on_time_percent?.toString() || '',
      sfv_triggered_appropriately_percent: metric.sfv_triggered_appropriately_percent?.toString() || '',
      moderate_severe_followups_completed_percent: metric.moderate_severe_followups_completed_percent?.toString() || '',
      timely_idg_completion_percent: metric.timely_idg_completion_percent?.toString() || '',
      wounds_count: metric.wounds_count?.toString() || '',
      wounds_healing_percent: metric.wounds_healing_percent?.toString() || '',
      falls_total: metric.falls_total?.toString() || '',
      infections_count: metric.infections_count?.toString() || '',
      nursing_visit_gaps_7days: metric.nursing_visit_gaps_7days?.toString() || '',
      gip_utilization_percent: metric.gip_utilization_percent?.toString() || '',
      chc_utilization_percent: metric.chc_utilization_percent?.toString() || '',
      burdensome_transitions: metric.burdensome_transitions?.toString() || '',
      cahps_recommendation_score: metric.cahps_recommendation_score?.toString() || '',
      // HVLDL metrics
      hvldl_2_days_percent: metric.hvldl_2_days_percent?.toString() || '',
      hvldl_3_days_percent: metric.hvldl_3_days_percent?.toString() || '',
      hvldl_7_days_percent: metric.hvldl_7_days_percent?.toString() || '',
      hvldl_median_visits_last_week: metric.hvldl_median_visits_last_week?.toString() || '',
      // Census & LOS
      median_length_of_stay: metric.median_length_of_stay?.toString() || '',
      mean_length_of_stay: metric.mean_length_of_stay?.toString() || '',
      median_adc: metric.median_adc?.toString() || '',
      live_discharges_percent: metric.live_discharges_percent?.toString() || '',
      total_adc: metric.total_adc?.toString() || ''
    });
    setPeriodDate(metric.period_date);
    setPeriodType(metric.period_type);
    setEditingMetric(metric);
    setHistoryDialogOpen(false);
  };

  // Upload and analyze data
  const handleUploadAnalyze = async () => {
    if (!uploadFile && !uploadText.trim()) {
      toast.error('Please upload a file or paste data');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      if (uploadFile) {
        formData.append('file', uploadFile);
      }
      if (uploadText.trim()) {
        formData.append('text_content', uploadText);
      }
      formData.append('data_type', uploadDataType);

      const response = await fetch(`${API_URL}/api/upload-analyze/data-entry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setAnalysisResult(result);
        toast.success('Data analyzed successfully');
      } else {
        toast.error('Failed to analyze data');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCreatePIP = async (pipIndex) => {
    if (!analysisResult?.id) return;
    
    try {
      const response = await fetch(`${API_URL}/api/upload-analyze/${analysisResult.id}/create-pip?pip_index=${pipIndex}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`PIP "${result.pip.title}" created successfully`);
      } else {
        toast.error('Failed to create PIP');
      }
    } catch (error) {
      toast.error('Error creating PIP');
    }
  };

  const resetUpload = () => {
    setUploadFile(null);
    setUploadText('');
    setAnalysisResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const tabs = [
    { id: 'clinical', label: 'Clinical Quality', icon: Stethoscope },
    { id: 'pipeline', label: 'Facility Pipeline', icon: Building2 },
    { id: 'safety', label: 'Safety & Incidents', icon: Shield },
    { id: 'regulatory', label: 'Regulatory', icon: FileText },
    { id: 'patient', label: 'Patient Experience', icon: Heart },
    { id: 'staff', label: 'Staff Health', icon: Briefcase },
    { id: 'upload', label: 'Upload & Analyze', icon: Upload },
    { id: 'data-history', label: 'Data History', icon: Clock }
  ];

  return (
    <div className="space-y-6" data-testid="data-entry-page">
      {/* Modern Header */}
      <PageHeader
        icon={ClipboardList}
        title="Data Entry"
        subtitle="Enter weekly and monthly quality metrics"
        gradient="from-amber-600 via-orange-500 to-red-500"
      >
        <div className="flex gap-2">
          <ExportDropdown reportType="clinical" token={token} />
          <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0" onClick={() => { fetchHistory(); setHistoryDialogOpen(true); }}>
            <History className="h-4 w-4 mr-2" />
            View History
          </Button>
        </div>
      </PageHeader>

      {/* Period Selection */}
      <ModernCard className="overflow-hidden">
        <div className="bg-gradient-to-r from-amber-600 to-orange-500 p-6 rounded-t-xl">
          <div className="flex flex-wrap items-end gap-6">
            <div className="space-y-2">
              <Label className="text-white/80 text-sm">Reporting Period</Label>
              <Select value={periodType} onValueChange={setPeriodType}>
                <SelectTrigger className="w-40 bg-white/10 border-white/20 text-white" data-testid="period-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white/80 text-sm">Period End Date</Label>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-white/70" />
                <Input
                  type="date"
                  value={periodDate}
                  onChange={(e) => setPeriodDate(e.target.value)}
                  className="w-44 bg-white/10 border-white/20 text-white"
                  data-testid="period-date-input"
                />
              </div>
            </div>
            <Badge className="h-10 px-4 flex items-center gap-2 bg-white/20 text-white border-0">
              <CheckCircle2 className="h-4 w-4" />
              {periodType} entry for {periodDate}
            </Badge>
            {editingMetric && (
              <Badge className="h-10 px-4 flex items-center gap-2 bg-orange-500 text-white border-0">
                <Edit2 className="h-4 w-4" />
                Editing existing entry
              </Badge>
            )}
          </div>
        </div>
      </ModernCard>

      {/* Data Entry Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0">
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2.5 rounded-xl border gap-2"
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Clinical Quality Tab - Enhanced */}
        <TabsContent value="clinical" className="space-y-6">
          <FormSection title="Symptom Management" description="Patient symptom control metrics" icon={Activity}>
            <FormField label="Pain Controlled Within 48h (%)" hint="Target: ≥95%">
              <Input type="number" step="0.1" max="100" value={clinicalData.pain_controlled_48h_percent}
                onChange={(e) => setClinicalData({ ...clinicalData, pain_controlled_48h_percent: e.target.value })}
                placeholder="e.g., 94.5" data-testid="input-pain-48h" />
              <ValidationWarning fieldName="pain_controlled_48h_percent" value={clinicalData.pain_controlled_48h_percent} validationTargets={validationTargets} />
            </FormField>
            <FormField label="Dyspnea Improvement Within 48h (%)" hint="Target: ≥90%">
              <Input type="number" step="0.1" max="100" value={clinicalData.dyspnea_improvement_48h_percent}
                onChange={(e) => setClinicalData({ ...clinicalData, dyspnea_improvement_48h_percent: e.target.value })}
                placeholder="e.g., 91.0" data-testid="input-dyspnea-48h" />
              <ValidationWarning fieldName="dyspnea_improvement_48h_percent" value={clinicalData.dyspnea_improvement_48h_percent} validationTargets={validationTargets} />
            </FormField>
            <FormField label="Anxiety/Agitation Improvement (%)">
              <Input type="number" step="0.1" max="100" value={clinicalData.anxiety_improvement_percent}
                onChange={(e) => setClinicalData({ ...clinicalData, anxiety_improvement_percent: e.target.value })}
                placeholder="e.g., 88.5" data-testid="input-anxiety" />
              <ValidationWarning fieldName="anxiety_improvement_percent" value={clinicalData.anxiety_improvement_percent} validationTargets={validationTargets} />
            </FormField>
          </FormSection>

          <FormSection title="Visits & Care Delivery" description="Visit completion and utilization" icon={Users}>
            <FormField label="Missed Visit Rate (%)" hint="Target: <5%">
              <Input type="number" step="0.1" max="100" value={clinicalData.missed_visit_rate_percent}
                onChange={(e) => setClinicalData({ ...clinicalData, missed_visit_rate_percent: e.target.value })}
                placeholder="e.g., 2.5" data-testid="input-missed-visits" />
            </FormField>
            <FormField label="Nursing Visit Gaps ≥7 Days" hint="Count of patients">
              <Input type="number" value={clinicalData.nursing_visit_gaps_7days}
                onChange={(e) => setClinicalData({ ...clinicalData, nursing_visit_gaps_7days: e.target.value })}
                placeholder="0" data-testid="input-visit-gaps" />
            </FormField>
            <FormField label="GIP Utilization (%)">
              <Input type="number" step="0.1" max="100" value={clinicalData.gip_utilization_percent}
                onChange={(e) => setClinicalData({ ...clinicalData, gip_utilization_percent: e.target.value })}
                placeholder="e.g., 5.2" data-testid="input-gip" />
            </FormField>
            <FormField label="CHC Utilization (%)">
              <Input type="number" step="0.1" max="100" value={clinicalData.chc_utilization_percent}
                onChange={(e) => setClinicalData({ ...clinicalData, chc_utilization_percent: e.target.value })}
                placeholder="e.g., 3.8" data-testid="input-chc" />
            </FormField>
          </FormSection>

          <FormSection title="Quality Indicators" description="Enhanced clinical quality metrics" icon={TrendingUp}>
            <FormField label="Wounds (Total Count)">
              <Input type="number" value={clinicalData.wounds_count}
                onChange={(e) => setClinicalData({ ...clinicalData, wounds_count: e.target.value })}
                placeholder="0" data-testid="input-wounds" />
            </FormField>
            <FormField label="Wounds Healing (%)">
              <Input type="number" step="0.1" max="100" value={clinicalData.wounds_healing_percent}
                onChange={(e) => setClinicalData({ ...clinicalData, wounds_healing_percent: e.target.value })}
                placeholder="e.g., 75.0" data-testid="input-wounds-healing" />
            </FormField>
            <FormField label="Falls (Total)">
              <Input type="number" value={clinicalData.falls_total}
                onChange={(e) => setClinicalData({ ...clinicalData, falls_total: e.target.value })}
                placeholder="0" data-testid="input-falls" />
              <ValidationWarning fieldName="falls_total" value={clinicalData.falls_total} validationTargets={validationTargets} />
            </FormField>
            <FormField label="Infections (Count)">
              <Input type="number" value={clinicalData.infections_count}
                onChange={(e) => setClinicalData({ ...clinicalData, infections_count: e.target.value })}
                placeholder="0" data-testid="input-infections" />
            </FormField>
            <FormField label="Burdensome Transitions" hint="Live DC within 7 days of admission">
              <Input type="number" value={clinicalData.burdensome_transitions}
                onChange={(e) => setClinicalData({ ...clinicalData, burdensome_transitions: e.target.value })}
                placeholder="0" data-testid="input-burdensome" />
            </FormField>
            <FormField label="CAHPS Recommendation Score" hint="Would recommend hospice">
              <Input type="number" step="0.1" max="100" value={clinicalData.cahps_recommendation_score}
                onChange={(e) => setClinicalData({ ...clinicalData, cahps_recommendation_score: e.target.value })}
                placeholder="e.g., 92.0" data-testid="input-cahps-rec" />
            </FormField>
          </FormSection>

          <FormSection title="Census & Length of Stay" description="Program-level clinical metrics" icon={Activity}>
            <FormField label="Total ADC">
              <Input type="number" step="0.1" value={clinicalData.total_adc}
                onChange={(e) => setClinicalData({ ...clinicalData, total_adc: e.target.value })}
                placeholder="e.g., 52.5" data-testid="input-total-adc" />
            </FormField>
            <FormField label="Median ADC">
              <Input type="number" step="0.1" value={clinicalData.median_adc}
                onChange={(e) => setClinicalData({ ...clinicalData, median_adc: e.target.value })}
                placeholder="e.g., 50.0" data-testid="input-median-adc" />
            </FormField>
            <FormField label="Median Length of Stay (days)">
              <Input type="number" step="0.1" value={clinicalData.median_length_of_stay}
                onChange={(e) => setClinicalData({ ...clinicalData, median_length_of_stay: e.target.value })}
                placeholder="e.g., 18.5" data-testid="input-median-los" />
            </FormField>
            <FormField label="Mean Length of Stay (days)">
              <Input type="number" step="0.1" value={clinicalData.mean_length_of_stay}
                onChange={(e) => setClinicalData({ ...clinicalData, mean_length_of_stay: e.target.value })}
                placeholder="e.g., 45.2" data-testid="input-mean-los" />
            </FormField>
            <FormField label="Live Discharges (%)">
              <Input type="number" step="0.1" max="100" value={clinicalData.live_discharges_percent}
                onChange={(e) => setClinicalData({ ...clinicalData, live_discharges_percent: e.target.value })}
                placeholder="e.g., 12.5" data-testid="input-live-dc" />
            </FormField>
          </FormSection>

          <FormSection title="HVLDL - Hospice Visits in Last Days of Life" description="End-of-life care quality metrics" icon={Heart}>
            <FormField label="RN Visit in Last 2 Days (%)" hint="CMS Quality Measure - Target: ≥90%">
              <Input type="number" step="0.1" max="100" value={clinicalData.hvldl_2_days_percent}
                onChange={(e) => setClinicalData({ ...clinicalData, hvldl_2_days_percent: e.target.value })}
                placeholder="e.g., 92.0" data-testid="input-hvldl-2day" />
              <ValidationWarning fieldName="hvldl_2_days_percent" value={clinicalData.hvldl_2_days_percent} validationTargets={validationTargets} />
            </FormField>
            <FormField label="RN Visit in Last 3 Days (%)" hint="Target: ≥95%">
              <Input type="number" step="0.1" max="100" value={clinicalData.hvldl_3_days_percent}
                onChange={(e) => setClinicalData({ ...clinicalData, hvldl_3_days_percent: e.target.value })}
                placeholder="e.g., 96.0" data-testid="input-hvldl-3day" />
              <ValidationWarning fieldName="hvldl_3_days_percent" value={clinicalData.hvldl_3_days_percent} validationTargets={validationTargets} />
            </FormField>
            <FormField label="Any Visit in Last 7 Days (%)" hint="Target: ≥98%">
              <Input type="number" step="0.1" max="100" value={clinicalData.hvldl_7_days_percent}
                onChange={(e) => setClinicalData({ ...clinicalData, hvldl_7_days_percent: e.target.value })}
                placeholder="e.g., 99.0" data-testid="input-hvldl-7day" />
            </FormField>
            <FormField label="Median Visits in Final Week" hint="Average visits during last 7 days of life">
              <Input type="number" step="0.1" value={clinicalData.hvldl_median_visits_last_week}
                onChange={(e) => setClinicalData({ ...clinicalData, hvldl_median_visits_last_week: e.target.value })}
                placeholder="e.g., 4.5" data-testid="input-hvldl-median" />
            </FormField>
          </FormSection>

          <FormSection title="HOPE / Clinical Compliance" description="Documentation compliance" icon={FileText}>
            <FormField label="HOPE Completed On Time (%)" hint="Target: ≥95%">
              <Input type="number" step="0.1" max="100" value={clinicalData.hope_completed_on_time_percent}
                onChange={(e) => setClinicalData({ ...clinicalData, hope_completed_on_time_percent: e.target.value })}
                placeholder="e.g., 98.0" data-testid="input-hope" />
            </FormField>
            <FormField label="Timely IDG Completion (%)" hint="Target: 100%">
              <Input type="number" step="0.1" max="100" value={clinicalData.timely_idg_completion_percent}
                onChange={(e) => setClinicalData({ ...clinicalData, timely_idg_completion_percent: e.target.value })}
                placeholder="e.g., 100" data-testid="input-idg" />
            </FormField>
          </FormSection>

          <div className="flex justify-end gap-3">
            {editingMetric && (
              <Button variant="outline" onClick={() => { setEditingMetric(null); setClinicalData({}); }}>
                Cancel Edit
              </Button>
            )}
            <Button 
              onClick={() => saveData('clinical', clinicalData, setClinicalData)}
              disabled={saving}
              className="min-w-32"
              data-testid="save-clinical-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : editingMetric ? 'Update Clinical Data' : 'Save Clinical Data'}
            </Button>
          </div>
        </TabsContent>

        {/* Facility Pipeline Tab - Top 10 Referral Sources */}
        <TabsContent value="pipeline" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-[#00b0ab]" />
                Top 10 Referral Sources
              </CardTitle>
              <CardDescription>
                Track your primary referral facilities, their volume, and conversion performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">#</TableHead>
                      <TableHead>Facility Name</TableHead>
                      <TableHead className="w-24">Referrals</TableHead>
                      <TableHead className="w-24">Conversions</TableHead>
                      <TableHead className="w-20">Rate</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referralSources.map((source, index) => {
                      const rate = source.referrals && source.conversions 
                        ? ((parseFloat(source.conversions) / parseFloat(source.referrals)) * 100).toFixed(0)
                        : '-';
                      return (
                        <TableRow key={index}>
                          <TableCell className="font-medium text-muted-foreground">{index + 1}</TableCell>
                          <TableCell>
                            <Input 
                              placeholder={`Facility ${index + 1}`}
                              value={source.facility_name}
                              onChange={(e) => {
                                const updated = [...referralSources];
                                updated[index].facility_name = e.target.value;
                                setReferralSources(updated);
                              }}
                              className="h-8"
                              data-testid={`input-facility-name-${index}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number"
                              placeholder="0"
                              value={source.referrals}
                              onChange={(e) => {
                                const updated = [...referralSources];
                                updated[index].referrals = e.target.value;
                                setReferralSources(updated);
                              }}
                              className="h-8 w-20"
                              data-testid={`input-facility-referrals-${index}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number"
                              placeholder="0"
                              value={source.conversions}
                              onChange={(e) => {
                                const updated = [...referralSources];
                                updated[index].conversions = e.target.value;
                                setReferralSources(updated);
                              }}
                              className="h-8 w-20"
                              data-testid={`input-facility-conversions-${index}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`${
                              rate !== '-' && parseFloat(rate) >= 70 ? 'border-emerald-500 text-emerald-600' :
                              rate !== '-' && parseFloat(rate) >= 50 ? 'border-amber-500 text-amber-600' :
                              rate !== '-' ? 'border-red-500 text-red-600' : ''
                            }`}>
                              {rate !== '-' ? `${rate}%` : '-'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Input 
                              placeholder="Notes..."
                              value={source.notes}
                              onChange={(e) => {
                                const updated = [...referralSources];
                                updated[index].notes = e.target.value;
                                setReferralSources(updated);
                              }}
                              className="h-8"
                              data-testid={`input-facility-notes-${index}`}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 pt-4 border-t">
                  <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-900">
                    <p className="text-xs text-muted-foreground">Total Referrals</p>
                    <p className="text-2xl font-bold">
                      {referralSources.reduce((sum, s) => sum + (parseFloat(s.referrals) || 0), 0)}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-900">
                    <p className="text-xs text-muted-foreground">Total Conversions</p>
                    <p className="text-2xl font-bold">
                      {referralSources.reduce((sum, s) => sum + (parseFloat(s.conversions) || 0), 0)}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-900">
                    <p className="text-xs text-muted-foreground">Overall Conversion Rate</p>
                    <p className="text-2xl font-bold">
                      {(() => {
                        const totalRef = referralSources.reduce((sum, s) => sum + (parseFloat(s.referrals) || 0), 0);
                        const totalConv = referralSources.reduce((sum, s) => sum + (parseFloat(s.conversions) || 0), 0);
                        return totalRef > 0 ? `${((totalConv / totalRef) * 100).toFixed(1)}%` : '-';
                      })()}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-900">
                    <p className="text-xs text-muted-foreground">Active Sources</p>
                    <p className="text-2xl font-bold">
                      {referralSources.filter(s => s.facility_name).length}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button 
              onClick={async () => {
                setSaving(true);
                try {
                  const response = await fetch(`${API_URL}/api/metrics/referral-sources`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                      sources: referralSources.filter(s => s.facility_name),
                      period_type: periodType,
                      period_date: periodDate
                    })
                  });
                  if (response.ok) {
                    toast.success('Referral sources saved successfully');
                  } else {
                    toast.error('Failed to save referral sources');
                  }
                } catch (error) {
                  toast.error('Connection error');
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
              className="min-w-32"
              data-testid="save-pipeline-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Referral Sources'}
            </Button>
          </div>
        </TabsContent>

        {/* Safety Tab */}
        <TabsContent value="safety" className="space-y-6">
          <FormSection title="Falls & Incidents" description="Patient safety events" icon={AlertTriangle}>
            <FormField label="Falls with Injury">
              <Input type="number" value={safetyData.falls_with_injury}
                onChange={(e) => setSafetyData({ ...safetyData, falls_with_injury: e.target.value })}
                placeholder="0" data-testid="input-falls-injury" />
            </FormField>
            <FormField label="Falls without Injury">
              <Input type="number" value={safetyData.falls_without_injury}
                onChange={(e) => setSafetyData({ ...safetyData, falls_without_injury: e.target.value })}
                placeholder="0" data-testid="input-falls-no-injury" />
            </FormField>
            <FormField label="Medication Errors (Near Miss)">
              <Input type="number" value={safetyData.medication_errors_near_miss}
                onChange={(e) => setSafetyData({ ...safetyData, medication_errors_near_miss: e.target.value })}
                placeholder="0" data-testid="input-med-near-miss" />
            </FormField>
            <FormField label="Medication Errors (Actual Harm)">
              <Input type="number" value={safetyData.medication_errors_actual_harm}
                onChange={(e) => setSafetyData({ ...safetyData, medication_errors_actual_harm: e.target.value })}
                placeholder="0" data-testid="input-med-harm" />
            </FormField>
            <FormField label="Hospitalizations">
              <Input type="number" value={safetyData.hospitalizations}
                onChange={(e) => setSafetyData({ ...safetyData, hospitalizations: e.target.value })}
                placeholder="0" data-testid="input-hospitalizations" />
            </FormField>
            <FormField label="ER Visits">
              <Input type="number" value={safetyData.er_visits}
                onChange={(e) => setSafetyData({ ...safetyData, er_visits: e.target.value })}
                placeholder="0" data-testid="input-er-visits" />
            </FormField>
          </FormSection>

          <div className="flex justify-end">
            <Button onClick={() => saveData('safety', safetyData, setSafetyData)} disabled={saving}
              className="min-w-32" data-testid="save-safety-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Safety Data'}
            </Button>
          </div>
        </TabsContent>

        {/* Regulatory Tab */}
        <TabsContent value="regulatory" className="space-y-6">
          <FormSection title="Documentation Timeliness" description="Compliance audit results" icon={FileText}>
            <FormField label="CTI Timeliness (%)" hint="Target: 100%">
              <Input type="number" step="0.1" max="100" value={regulatoryData.cti_timeliness_percent}
                onChange={(e) => setRegulatoryData({ ...regulatoryData, cti_timeliness_percent: e.target.value })}
                placeholder="100" data-testid="input-cti" />
            </FormField>
            <FormField label="F2F Completed in Correct Window (%)" hint="Target: 100%">
              <Input type="number" step="0.1" max="100" value={regulatoryData.f2f_correct_window_percent}
                onChange={(e) => setRegulatoryData({ ...regulatoryData, f2f_correct_window_percent: e.target.value })}
                placeholder="100" data-testid="input-f2f" />
            </FormField>
            <FormField label="Documentation Completeness Score" hint="Target: ≥95%">
              <Input type="number" step="0.1" max="100" value={regulatoryData.documentation_completeness_score}
                onChange={(e) => setRegulatoryData({ ...regulatoryData, documentation_completeness_score: e.target.value })}
                placeholder="96" data-testid="input-doc-complete" />
            </FormField>
          </FormSection>

          <div className="flex justify-end">
            <Button onClick={() => saveData('regulatory', regulatoryData, setRegulatoryData)} disabled={saving}
              className="min-w-32" data-testid="save-regulatory-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Regulatory Data'}
            </Button>
          </div>
        </TabsContent>

        {/* Patient Experience Tab */}
        <TabsContent value="patient" className="space-y-6">
          <FormSection title="CAHPS Domains" description="Hospice CAHPS survey scores" icon={Heart}>
            <FormField label="Communication Score">
              <Input type="number" step="0.1" max="100" value={patientExpData.cahps_communication}
                onChange={(e) => setPatientExpData({ ...patientExpData, cahps_communication: e.target.value })}
                placeholder="85" data-testid="input-cahps-comm" />
            </FormField>
            <FormField label="Pain & Symptom Help Score">
              <Input type="number" step="0.1" max="100" value={patientExpData.cahps_pain_symptom_help}
                onChange={(e) => setPatientExpData({ ...patientExpData, cahps_pain_symptom_help: e.target.value })}
                placeholder="88" data-testid="input-cahps-pain" />
            </FormField>
            <FormField label="Timeliness of Care Score">
              <Input type="number" step="0.1" max="100" value={patientExpData.cahps_timeliness_of_care}
                onChange={(e) => setPatientExpData({ ...patientExpData, cahps_timeliness_of_care: e.target.value })}
                placeholder="82" data-testid="input-cahps-timely" />
            </FormField>
          </FormSection>

          <div className="flex justify-end">
            <Button onClick={() => saveData('patient-experience', patientExpData, setPatientExpData)} disabled={saving}
              className="min-w-32" data-testid="save-patient-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Patient Experience Data'}
            </Button>
          </div>
        </TabsContent>

        {/* Staff Health Tab */}
        <TabsContent value="staff" className="space-y-6">
          <FormSection title="Turnover & Vacancies" description="Staff retention metrics" icon={Briefcase}>
            <FormField label="RN Turnover Rate (%)" hint="Target: <20%">
              <Input type="number" step="0.1" max="100" value={staffData.rn_turnover_rate_percent}
                onChange={(e) => setStaffData({ ...staffData, rn_turnover_rate_percent: e.target.value })}
                placeholder="15" data-testid="input-rn-turnover" />
            </FormField>
            <FormField label="RN Vacancies">
              <Input type="number" value={staffData.rn_vacancies}
                onChange={(e) => setStaffData({ ...staffData, rn_vacancies: e.target.value })}
                placeholder="0" data-testid="input-rn-vacancies" />
            </FormField>
            <FormField label="Overtime Hours">
              <Input type="number" step="0.5" value={staffData.overtime_hours}
                onChange={(e) => setStaffData({ ...staffData, overtime_hours: e.target.value })}
                placeholder="0" data-testid="input-overtime" />
            </FormField>
            <FormField label="Education Completion Rate (%)" hint="Target: ≥95%">
              <Input type="number" step="0.1" max="100" value={staffData.education_completion_rate_percent}
                onChange={(e) => setStaffData({ ...staffData, education_completion_rate_percent: e.target.value })}
                placeholder="98" data-testid="input-education" />
            </FormField>
            <FormField label="PRN Utilization (%)" hint="Target: <12% - PRN staff hours as % of total">
              <Input type="number" step="0.1" min="0" max="100" value={staffData.prn_utilization}
                onChange={(e) => setStaffData({ ...staffData, prn_utilization: e.target.value })}
                placeholder="9.2" data-testid="input-prn-utilization" />
            </FormField>
          </FormSection>

          <div className="flex justify-end">
            <Button onClick={() => saveData('staff-health', staffData, setStaffData)} disabled={saving}
              className="min-w-32" data-testid="save-staff-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Staff Data'}
            </Button>
          </div>
        </TabsContent>

        {/* Upload & Analyze Tab */}
        <TabsContent value="upload" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-[#00b0ab]" />
                Upload Data for Analysis
              </CardTitle>
              <CardDescription>
                Upload CSV files or paste data to extract metrics and get AI-powered insights
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data Type</Label>
                  <Select value={uploadDataType} onValueChange={setUploadDataType}>
                    <SelectTrigger data-testid="upload-data-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clinical">Clinical Metrics</SelectItem>
                      <SelectItem value="safety">Safety & Incidents</SelectItem>
                      <SelectItem value="regulatory">Regulatory Compliance</SelectItem>
                      <SelectItem value="program">Program Overview</SelectItem>
                      <SelectItem value="patient_experience">Patient Experience</SelectItem>
                      <SelectItem value="staff">Staff Health</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Upload File (CSV, TXT)</Label>
                  <Input 
                    type="file" 
                    accept=".csv,.txt,.xlsx"
                    ref={fileInputRef}
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    data-testid="upload-file-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Or Paste Data</Label>
                <Textarea 
                  placeholder="Paste your data here... (e.g., copy from spreadsheet or report)"
                  value={uploadText}
                  onChange={(e) => setUploadText(e.target.value)}
                  rows={6}
                  data-testid="upload-text-input"
                />
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleUploadAnalyze} 
                  disabled={uploading || (!uploadFile && !uploadText.trim())}
                  className="bg-gradient-to-r from-[#00b0ab] to-[#008a86]"
                  data-testid="analyze-upload-btn"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Brain className="h-4 w-4 mr-2" />
                  )}
                  {uploading ? 'Analyzing...' : 'Upload & Analyze'}
                </Button>
                {(uploadFile || uploadText || analysisResult) && (
                  <Button variant="outline" onClick={resetUpload}>
                    Clear
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Analysis Results */}
          {analysisResult && (
            <div className="space-y-4">
              {/* Extracted Data */}
              {analysisResult.extracted_data && Object.keys(analysisResult.extracted_data).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileUp className="h-5 w-5 text-emerald-500" />
                      Extracted Data
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {Object.entries(analysisResult.extracted_data)
                        .filter(([key]) => !['rows', 'columns', 'raw_content_preview'].includes(key))
                        .slice(0, 12)
                        .map(([key, value]) => (
                          <div key={key} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900">
                            <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                            <p className="font-semibold">{typeof value === 'number' ? value.toLocaleString() : String(value)}</p>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* AI Analysis */}
              {analysisResult.ai_analysis && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Sparkles className="h-5 w-5 text-[#00b0ab]" />
                      AI Analysis & Insights
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <AIContent content={analysisResult.ai_analysis} />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* PIP Recommendations */}
              {analysisResult.pip_recommendations && analysisResult.pip_recommendations.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Target className="h-5 w-5 text-orange-500" />
                      PIP Opportunities
                    </CardTitle>
                    <CardDescription>
                      Performance Improvement Projects suggested based on the data
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {analysisResult.pip_recommendations.map((pip, index) => (
                        <div 
                          key={index} 
                          className="flex items-center justify-between p-4 rounded-lg border bg-gradient-to-r from-orange-500/5 to-amber-500/5"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{pip.suggested_pip}</h4>
                              <Badge className={pip.priority === 'high' ? 'bg-red-500' : pip.priority === 'medium' ? 'bg-amber-500' : 'bg-blue-500'}>
                                {pip.priority}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{pip.description}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Current: <strong>{pip.current_value}</strong> | Threshold: <strong>{pip.threshold}</strong>
                            </p>
                          </div>
                          <Button 
                            size="sm"
                            onClick={() => handleCreatePIP(index)}
                            className="ml-4"
                            data-testid={`create-pip-btn-${index}`}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Create PIP
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Data History Tab */}
        <TabsContent value="data-history" className="space-y-6">
          <SectionHeader 
            icon={Clock} 
            title="Data Entry History" 
            subtitle="View timeline of all data entries across categories"
          />
          <div className="grid gap-4">
            <DataHistoryPanel 
              category="clinical" 
              title="Clinical Quality Data" 
              isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
            />
            <DataHistoryPanel 
              category="program" 
              title="Program Overview" 
              isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
            />
            <DataHistoryPanel 
              category="census" 
              title="Census Records" 
              isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
            />
            <DataHistoryPanel 
              category="compliance" 
              title="Compliance Data" 
              isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
            />
          </div>
        </TabsContent>
      </Tabs>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Clinical Metrics History</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-96">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Pain Ctrl</TableHead>
                  <TableHead>Missed Visits</TableHead>
                  <TableHead>HOPE</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {metricsHistory.map((metric) => (
                  <TableRow key={metric.id}>
                    <TableCell><Badge variant="outline">{metric.period_type}</Badge></TableCell>
                    <TableCell>{metric.period_date}</TableCell>
                    <TableCell>{metric.pain_controlled_48h_percent?.toFixed(1)}%</TableCell>
                    <TableCell>{metric.missed_visit_rate_percent?.toFixed(1)}%</TableCell>
                    <TableCell>{metric.hope_completed_on_time_percent?.toFixed(1)}%</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => loadMetricForEdit(metric)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteMetric(metric.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
